import { useEffect } from 'react'

type UseAutoSelectConversationParams = {
  conversations: ReadonlyArray<{ id: string }>
  /** The inbox has answered and no request is in flight. */
  settled: boolean
  selectedConversationId: string | undefined
  selectConversation: (conversationId: string) => void
  clearSelection: () => void
}

/**
 * Keeps the selection on a conversation the inbox holds, so the thread pane
 * never loads one the list cannot show.
 *
 * With nothing selected, the first conversation opens as soon as the list
 * has one, so the Messages tab never lands on an empty thread pane. A
 * selected id the list does not hold, such as one left in the URL from
 * another scope, waits for the inbox to settle, so a reload keeps the
 * conversation it was on; once settled it gives way to the first
 * conversation, or to no selection when the inbox is empty.
 *
 * Selection writes to the URL with `replace`, so this normalisation adds no
 * history entry that Back would have to step through.
 */
export function useAutoSelectConversation({
  conversations,
  settled,
  selectedConversationId,
  selectConversation,
  clearSelection,
}: UseAutoSelectConversationParams): void {
  const firstId = conversations[0]?.id
  const isSelectionListed =
    selectedConversationId !== undefined &&
    conversations.some(
      (conversation) => conversation.id === selectedConversationId,
    )

  useEffect(() => {
    if (isSelectionListed) return
    if (selectedConversationId && !settled) return
    if (firstId) {
      selectConversation(firstId)
    } else if (selectedConversationId) {
      clearSelection()
    }
  }, [
    isSelectionListed,
    selectedConversationId,
    settled,
    firstId,
    selectConversation,
    clearSelection,
  ])
}
